// A minimal ZIP writer, store method only (no compression).
//
// Written by hand rather than pulling in JSZip: the only thing going in these
// archives is PNGs, which are already deflate-compressed internally, so a
// compressing zip would spend time to save nothing. ~90 lines here against
// ~100KB on a bundle the build already flags as too large.
//
// Produces a standard archive — Windows Explorer, macOS Archive Utility and
// unzip all open it.

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    table[i] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i += 1) c = CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
};

// DOS date/time, which is what the ZIP header carries.
const dosStamp = (d = new Date()) => ({
  time: ((d.getHours() & 0x1F) << 11) | ((d.getMinutes() & 0x3F) << 5) | ((Math.floor(d.getSeconds() / 2)) & 0x1F),
  date: (((d.getFullYear() - 1980) & 0x7F) << 9) | (((d.getMonth() + 1) & 0x0F) << 5) | (d.getDate() & 0x1F),
});

const w16 = (v) => [v & 0xFF, (v >>> 8) & 0xFF];
const w32 = (v) => [v & 0xFF, (v >>> 8) & 0xFF, (v >>> 16) & 0xFF, (v >>> 24) & 0xFF];

/**
 * @param files [{ name, data }] where data is a Uint8Array or ArrayBuffer
 * @returns Blob of type application/zip
 */
export async function buildZip(files) {
  const encoder = new TextEncoder();
  const { time, date } = dosStamp();
  const chunks = [];
  const central = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = encoder.encode(file.name);
    const data = file.data instanceof Uint8Array ? file.data : new Uint8Array(file.data);
    const crc = crc32(data);

    const local = [
      ...w32(0x04034b50),      // local file header signature
      ...w16(20),              // version needed
      ...w16(0x0800),          // flags: UTF-8 names
      ...w16(0),               // method: store
      ...w16(time), ...w16(date),
      ...w32(crc),
      ...w32(data.length),     // compressed size
      ...w32(data.length),     // uncompressed size
      ...w16(nameBytes.length),
      ...w16(0),               // extra field length
    ];
    chunks.push(new Uint8Array(local), nameBytes, data);

    central.push({
      nameBytes, crc, size: data.length, offset, time, date,
    });
    offset += local.length + nameBytes.length + data.length;
  }

  const centralStart = offset;
  for (const e of central) {
    const header = [
      ...w32(0x02014b50),      // central directory signature
      ...w16(20), ...w16(20),  // version made by / needed
      ...w16(0x0800),
      ...w16(0),
      ...w16(e.time), ...w16(e.date),
      ...w32(e.crc),
      ...w32(e.size), ...w32(e.size),
      ...w16(e.nameBytes.length),
      ...w16(0), ...w16(0),    // extra, comment
      ...w16(0),               // disk number
      ...w16(0), ...w32(0),    // internal / external attrs
      ...w32(e.offset),
    ];
    chunks.push(new Uint8Array(header), e.nameBytes);
    offset += header.length + e.nameBytes.length;
  }

  const end = [
    ...w32(0x06054b50),
    ...w16(0), ...w16(0),
    ...w16(central.length), ...w16(central.length),
    ...w32(offset - centralStart),
    ...w32(centralStart),
    ...w16(0),
  ];
  chunks.push(new Uint8Array(end));

  return new Blob(chunks, { type: 'application/zip' });
}

/** Save a Blob to disk under `filename`. */
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoked on the next tick — revoking immediately cancels the download in
  // Safari, which has not started reading the blob yet.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
